import {
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from "@chakra-ui/react";

import { format } from "date-fns";
import { FiSearch } from "react-icons/fi";
import { LuDownload, LuFileSymlink } from "react-icons/lu";
import { MdOutlinePictureAsPdf } from "react-icons/md";
import { TbFileDescription } from "react-icons/tb";

import { IBankStatementDocument } from "@/jenesys-types/jenesys-entities/config";

import { EmptyState } from "@/components/features/settings/EmptyState";
import { useBankStatementDocuments } from "@/hooks/features/useBankStatementDocuments";
import { useCompany } from "@/hooks/util/useCompany";

import DownloadCSV from "./DownloadCSV";
import { UploadPDF } from "./UploadPDF";

export interface BankStatementDocumentsProps {}

export const BankStatementDocuments: React.FC<
  BankStatementDocumentsProps
> = () => {
  const { activeClientId } = useCompany();
  const data = useBankStatementDocuments({ clientId: activeClientId });

  const {
    statements,
    searchQuery,
    isDownloadingCSV,
    isDownloadingPDF,
    debouncedOnChange,
    selectedStatements,
    uploadPDFDisclosure,
    isFetchingStatements,
    downloadCSVsDisclosure,
    selectionsAreDownloadable,
    handleDownloadCSV,
    handleDownloadPDF,
    handleToggleSelection,
    handleToggleSelectAll,
    isUploading,
    handleSubmitFiles,
    handleUploadStatements,
    files,
    setFiles
  } = data;

  return (
    <Box mt={8} rounded={16} px={10} pt={7} pb={16} bg="white">
      <HStack align="flex-end" justifyContent="space-between">
        <Box>
          <Heading fontSize={16} mb={4} fontWeight={600}>
            Documents
          </Heading>
          <Text color="secondary.700" fontSize={13}>
            Easily analyse your bank statements by converting PDFs to structured
            CSV data. Click on the icons below to individually download the bank
            statements you require.
          </Text>
        </Box>

        <HStack spacing={2}>
          <Button
            leftIcon={<LuFileSymlink />}
            onClick={uploadPDFDisclosure.onOpen}
            h={9}
            w={176}
            size="sm"
          >
            Convert to CSV
          </Button>

          <Button
            leftIcon={<LuDownload />}
            onClick={downloadCSVsDisclosure.onOpen}
            isDisabled={
              !selectedStatements.length || !selectionsAreDownloadable
            }
            h={9}
            w={176}
            size="sm"
          >
            Download CSV
          </Button>
        </HStack>
      </HStack>

      <HStack mt={5} justify="space-between">
        <HStack w="full">
          <InputGroup w="full" borderColor="blackAlpha.300" h={9}>
            <InputLeftElement top={-0.5} pointerEvents="none">
              <FiSearch fontSize={18} color="gray.500" />
            </InputLeftElement>
            <Input
              onChange={(event) => {
                debouncedOnChange(event.target.value);
              }}
              fontSize={14}
              h={9}
              type="search"
              placeholder="Search..."
            />
          </InputGroup>
        </HStack>
      </HStack>

      <Flex
        display={["flex", "flex", "flex", "flex", "block"]}
        direction="row"
        w="full"
        overflow={["auto"]}
      >
        <TableContainer flexShrink={0} position="relative">
          {(isFetchingStatements || isUploading) && (
            <Center
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="whiteAlpha.700"
            >
              <Spinner size="md" color="primary.500" />
            </Center>
          )}
          <Table mt={5} size="sm">
            <Thead>
              <Tr>
                <Th pl={0}>
                  <Checkbox
                    size="lg"
                    borderColor="blackAlpha.700"
                    colorScheme="primary"
                    isChecked={
                      !!statements.length &&
                      selectedStatements.length === statements.length
                    }
                    onChange={handleToggleSelectAll}
                  />
                </Th>
                <Th color="black" textTransform="capitalize" fontWeight={600}>
                  File name
                </Th>
                <Th color="black" textTransform="capitalize" fontWeight={600}>
                  Uploader
                </Th>
                <Th
                  w={56}
                  color="black"
                  textTransform="capitalize"
                  fontWeight={600}
                >
                  Date and Time
                </Th>
                <Th
                  maxW={8}
                  color="black"
                  textTransform="capitalize"
                  fontWeight={600}
                >
                  Status
                </Th>
                <Th color="black" textTransform="capitalize" fontWeight={600}>
                  CSV File
                </Th>
                <Th
                  maxW={8}
                  color="black"
                  textTransform="capitalize"
                  fontWeight={600}
                >
                  PDF
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {statements.map((statement: IBankStatementDocument) => {
                const {
                  id: statementId,
                  filename,
                  user,
                  created_at: createdAt
                } = statement;
                const isSelected = !!selectedStatements.find(
                  ({ id }) => id === statementId
                );

                return (
                  <Tr key={statementId} _hover={{ bg: "blackAlpha.50" }}>
                    <Td pl={0} py={1.5}>
                      <Checkbox
                        isChecked={isSelected}
                        onChange={(event) =>
                          handleToggleSelection(event, statement)
                        }
                        rounded={12}
                        borderColor="blackAlpha.600"
                        size="lg"
                        colorScheme="primary"
                      />
                    </Td>

                    <Td fontWeight={500} py={1.5}>
                      {filename}
                    </Td>
                    <Td fontWeight={500} py={1.5}>
                      {user.first_name} {user.last_name}
                    </Td>
                    <Td fontWeight={500} py={1.5}>
                      {format(createdAt, "d MMMM yyyy, h:mm a")}
                    </Td>
                    <Td fontWeight={500} py={1.5}>
                      {statement.status}
                    </Td>
                    <Td fontWeight={500} py={1.5}>
                      {statement.download_csv_url && (
                        <IconButton
                          isLoading={isDownloadingCSV}
                          onClick={() => handleDownloadCSV([statement])}
                          aria-label="Download CSV"
                          fontSize={20}
                          icon={<TbFileDescription />}
                          size="sm"
                          variant="default"
                        />
                      )}
                    </Td>

                    <Td fontWeight={500} py={1.5}>
                      {statement.download_pdf_url && (
                        <IconButton
                          isLoading={isDownloadingPDF}
                          onClick={() => handleDownloadPDF([statement])}
                          aria-label="Download PDF"
                          fontSize={20}
                          icon={<MdOutlinePictureAsPdf />}
                          size="sm"
                          variant="default"
                        />
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>

          {!statements.length && !searchQuery && !isFetchingStatements && (
            <EmptyState
              title="Upload a new document"
              ctaText="Upload document"
              ctaAction={uploadPDFDisclosure.onOpen}
              description="When you upload a new document, it will appear here"
              mt={16}
            />
          )}
        </TableContainer>
      </Flex>

      {uploadPDFDisclosure.isOpen && (
        <UploadPDF
          isOpen={uploadPDFDisclosure.isOpen}
          onClose={uploadPDFDisclosure.onClose}
          isUploading={isUploading}
          handleSubmitFiles={handleSubmitFiles}
          handleUploadFiles={handleUploadStatements}
          files={files}
          setFiles={setFiles}
        />
      )}

      {downloadCSVsDisclosure.isOpen && (
        <DownloadCSV
          {...downloadCSVsDisclosure}
          {...data}
          handleDownloadClick={() => handleDownloadCSV(selectedStatements)}
        />
      )}
    </Box>
  );
};
